<script setup>
import { ref, computed, onMounted, provide, watch, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

// APIs
import ReportsAPI from 'dashboard/api/reports';
import AnalyticsDashboardAPI from 'dashboard/api/analyticsDashboard';

// Constants
const REFRESH_INTERVAL = 30000; // 30 seconds refresh

// Tab Views
import OverviewTab from './views/OverviewTab.vue';
import ConversationsTab from './views/ConversationsTab.vue';
import AgentsTab from './views/AgentsTab.vue';
import ChannelsTab from './views/ChannelsTab.vue';
import PerformanceTab from './views/PerformanceTab.vue';
import CrmTab from './views/CrmTab.vue';
import MonitorTab from './views/MonitorTab.vue';

const { t } = useI18n();
const store = useStore();

// Getters
const accountConversationMetric = useMapGetter('getAccountConversationMetric');
const agentStatus = useMapGetter('agents/getAgentStatus');
const uiFlags = useMapGetter('getOverviewUIFlags');
const agents = useMapGetter('agents/getAgents');
const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabels');
const currentAccountId = useMapGetter('getCurrentAccountId');

// Tabs configuration
const tabs = [
  {
    key: 'overview',
    label: 'ANALYTICS_DASHBOARD.TABS.OVERVIEW',
    icon: 'i-lucide-layout-grid',
  },
  {
    key: 'conversations',
    label: 'ANALYTICS_DASHBOARD.TABS.CONVERSATIONS',
    icon: 'i-lucide-message-square',
  },
  {
    key: 'agents',
    label: 'ANALYTICS_DASHBOARD.TABS.AGENTS',
    icon: 'i-lucide-users',
  },
  {
    key: 'channels',
    label: 'ANALYTICS_DASHBOARD.TABS.CHANNELS',
    icon: 'i-lucide-inbox',
  },
  {
    key: 'performance',
    label: 'ANALYTICS_DASHBOARD.TABS.PERFORMANCE',
    icon: 'i-lucide-trending-up',
  },
  {
    key: 'crm',
    label: 'ANALYTICS_DASHBOARD.TABS.CRM',
    icon: 'i-lucide-building',
  },
  {
    key: 'monitor',
    label: 'ANALYTICS_DASHBOARD.TABS.MONITOR',
    icon: 'i-lucide-monitor',
  },
];

// Period options
const periodOptions = [
  { key: 'last_7_days', label: '7 dias', days: 7 },
  { key: 'last_30_days', label: '30 dias', days: 30 },
  { key: 'last_90_days', label: '90 dias', days: 90 },
  { key: 'last_year', label: '12 meses', days: 365 },
];

// State
const activeTab = ref('overview');
const selectedPeriod = ref('last_30_days');
const isLoading = ref(false);
const dateRange = ref(getDateRangeForPeriod('last_30_days'));

// Report data
const summaryData = ref(null);
const conversationData = ref([]);
const kanbanSummary = ref(null);
const csatMetrics = ref(null);
const previousSummaryData = ref(null);
const botMetrics = ref(null);
const slaMetrics = ref(null);
const agentReports = ref([]);
const inboxReports = ref([]);
const labelReports = ref([]);
const campaignData = ref(null);
const aiAgentData = ref(null);
const crmData = ref(null);
const liveMonitorData = ref(null);
const conversationDetails = ref(null);

// Detailed data for each tab
const conversationsDetailed = ref(null);
const agentsDetailed = ref(null);
const inboxesDetailed = ref(null);
const systemPerformance = ref(null);
const crmDetailed = ref(null);

// Refresh timer
const refreshTimerId = ref(null);
const lastUpdated = ref(new Date());

// Computed
const conversationMetrics = computed(() => {
  const metrics = accountConversationMetric.value || {};
  return {
    open: metrics.open || 0,
    unattended: metrics.unattended || 0,
    unassigned: metrics.unassigned || 0,
    pending: metrics.pending || 0,
  };
});

const agentStatusMetrics = computed(() => {
  const status = agentStatus.value || {};
  return {
    online: status.online || 0,
    busy: status.busy || 0,
    offline: status.offline || 0,
  };
});

const totalAgentsOnline = computed(() => {
  return agentStatusMetrics.value.online + agentStatusMetrics.value.busy;
});

const activeTabComponent = computed(() => {
  const componentMap = {
    overview: OverviewTab,
    conversations: ConversationsTab,
    agents: AgentsTab,
    channels: ChannelsTab,
    performance: PerformanceTab,
    crm: CrmTab,
    monitor: MonitorTab,
  };
  return componentMap[activeTab.value] || OverviewTab;
});

// Percentage change calculation
const conversationsChange = computed(() => {
  if (!summaryData.value || !previousSummaryData.value) return null;
  const current = summaryData.value.conversations_count || 0;
  const previous = previousSummaryData.value.conversations_count || 0;
  if (previous === 0) return current > 0 ? 100 : 0;
  return (((current - previous) / previous) * 100).toFixed(1);
});

const resolutionsChange = computed(() => {
  if (!summaryData.value || !previousSummaryData.value) return null;
  const current = summaryData.value.resolutions_count || 0;
  const previous = previousSummaryData.value.resolutions_count || 0;
  if (previous === 0) return current > 0 ? 100 : 0;
  return (((current - previous) / previous) * 100).toFixed(1);
});

// Methods
function getDateRangeForPeriod(period) {
  const now = new Date();
  let from = new Date();
  const periodConfig = periodOptions.find(p => p.key === period);
  const days = periodConfig?.days || 30;

  from.setDate(now.getDate() - days);

  return {
    from: Math.floor(from.getTime() / 1000),
    to: Math.floor(now.getTime() / 1000),
  };
}

function getPreviousDateRange(currentRange, period) {
  const periodConfig = periodOptions.find(p => p.key === period);
  const days = periodConfig?.days || 30;

  const from = new Date((currentRange.from - days * 24 * 60 * 60) * 1000);
  const to = new Date((currentRange.to - days * 24 * 60 * 60) * 1000);

  return {
    from: Math.floor(from.getTime() / 1000),
    to: Math.floor(to.getTime() / 1000),
  };
}

async function fetchLiveMetrics() {
  try {
    store.dispatch('fetchAccountConversationMetric');
    store.dispatch('agents/get');
  } catch (error) {
    console.error('Error fetching live metrics:', error);
  }
}

async function fetchAllReports(showLoading = true) {
  if (showLoading) {
    isLoading.value = true;
  }

  try {
    const { from, to } = dateRange.value;
    const timezoneOffset = -new Date().getTimezoneOffset() / 60;
    const params = { since: from, until: to, timezone_offset: timezoneOffset };

    // Fetch all data in parallel for maximum performance
    const [
      overviewResponse,
      campaignsResponse,
      aiAgentsResponse,
      crmResponse,
      conversationsDetailedResponse,
      agentsDetailedResponse,
      inboxesDetailedResponse,
      systemPerformanceResponse,
      crmDetailedResponse,
      liveMonitorResponse,
    ] = await Promise.all([
      AnalyticsDashboardAPI.getOverview(params).catch(() => ({ data: null })),
      AnalyticsDashboardAPI.getCampaigns({ since: from, until: to }).catch(
        () => ({ data: null })
      ),
      AnalyticsDashboardAPI.getAiAgents({ since: from, until: to }).catch(
        () => ({ data: null })
      ),
      AnalyticsDashboardAPI.getCrm({ since: from, until: to }).catch(() => ({
        data: null,
      })),
      AnalyticsDashboardAPI.getConversationsDetailed(params).catch(() => ({
        data: null,
      })),
      AnalyticsDashboardAPI.getAgentsDetailed(params).catch(() => ({
        data: null,
      })),
      AnalyticsDashboardAPI.getInboxesDetailed(params).catch(() => ({
        data: null,
      })),
      AnalyticsDashboardAPI.getSystemPerformance(params).catch(() => ({
        data: null,
      })),
      AnalyticsDashboardAPI.getCrmDetailed(params).catch(() => ({
        data: null,
      })),
      AnalyticsDashboardAPI.getLiveMonitorRealtime().catch(() => ({
        data: null,
      })),
    ]);

    // Process overview response
    if (overviewResponse.data) {
      const data = overviewResponse.data;
      summaryData.value = data.summary;
      previousSummaryData.value = data.previous_summary;
      conversationDetails.value = data.conversations;
      agentReports.value = data.agents || [];
      inboxReports.value = data.inboxes || [];
      labelReports.value = data.labels || [];
      kanbanSummary.value = data.kanban;
      csatMetrics.value = data.csat;
      slaMetrics.value = data.sla;
      botMetrics.value = data.bot;
    }

    // Process other responses
    campaignData.value = campaignsResponse.data;
    aiAgentData.value = aiAgentsResponse.data;
    crmData.value = crmResponse.data;
    conversationsDetailed.value = conversationsDetailedResponse.data;
    agentsDetailed.value = agentsDetailedResponse.data;
    inboxesDetailed.value = inboxesDetailedResponse.data;
    systemPerformance.value = systemPerformanceResponse.data;
    crmDetailed.value = crmDetailedResponse.data;
    liveMonitorData.value = liveMonitorResponse.data;

    // Fetch conversation timeseries
    await fetchConversationChart();

    // Update last updated timestamp
    lastUpdated.value = new Date();
  } catch (error) {
    console.error('Error fetching reports:', error);
    if (showLoading) {
      useAlert(t('ANALYTICS_DASHBOARD.ERROR_LOADING'));
    }
  } finally {
    isLoading.value = false;
  }
}

async function fetchLiveMonitor() {
  try {
    const response = await AnalyticsDashboardAPI.getLiveMonitor();
    liveMonitorData.value = response.data;
  } catch (error) {
    console.error('Error fetching live monitor:', error);
  }
}

async function fetchConversationChart() {
  try {
    const { from, to } = dateRange.value;
    const response = await ReportsAPI.getReports({
      metric: 'conversations_count',
      from,
      to,
      type: 'account',
      groupBy: 'day',
    });
    conversationData.value = response.data || [];
  } catch {
    // Silently ignore errors (including cancellations)
  }
}

function onPeriodChange(period) {
  selectedPeriod.value = period;
  dateRange.value = getDateRangeForPeriod(period);
}

function setActiveTab(tabKey) {
  activeTab.value = tabKey;
}

function refreshData() {
  fetchLiveMetrics();
  fetchAllReports();
}

// Provide data to child components
provide('analyticsData', {
  // Summary data
  summaryData,
  previousSummaryData,
  conversationData,
  conversationDetails,

  // Agent, inbox, label reports
  agentReports,
  inboxReports,
  labelReports,

  // Metrics
  kanbanSummary,
  csatMetrics,
  botMetrics,
  slaMetrics,

  // Module-specific data
  campaignData,
  aiAgentData,
  crmData,
  liveMonitorData,

  // Detailed data for each tab
  conversationsDetailed,
  agentsDetailed,
  inboxesDetailed,
  systemPerformance,
  crmDetailed,

  // Computed metrics
  conversationMetrics,
  agentStatusMetrics,
  totalAgentsOnline,
  conversationsChange,
  resolutionsChange,

  // UI state
  isLoading,
  dateRange,
  selectedPeriod,
  lastUpdated,

  // Store data
  agents,
  inboxes,
  labels,

  // Actions
  refreshData,
  fetchLiveMonitor,
});

// Watch for period changes
watch(selectedPeriod, () => {
  dateRange.value = getDateRangeForPeriod(selectedPeriod.value);
});

watch(
  dateRange,
  () => {
    fetchAllReports(true);
  },
  { deep: true }
);

// Start real-time refresh (5 seconds)
function startRealTimeRefresh() {
  stopRealTimeRefresh();
  refreshTimerId.value = setInterval(() => {
    fetchLiveMetrics();
    fetchAllReports(false); // Don't show loading on auto-refresh
  }, REFRESH_INTERVAL);
}

function stopRealTimeRefresh() {
  if (refreshTimerId.value) {
    clearInterval(refreshTimerId.value);
    refreshTimerId.value = null;
  }
}

// Lifecycle
onMounted(() => {
  // Fetch initial data
  fetchLiveMetrics();
  fetchAllReports(true);

  // Fetch initial data for stores
  store.dispatch('inboxes/get');
  store.dispatch('labels/get');

  // Start 5-second real-time refresh
  startRealTimeRefresh();
});

onUnmounted(() => {
  stopRealTimeRefresh();
});
</script>

<template>
  <section
    class="flex flex-col w-full h-full min-w-0 overflow-hidden bg-n-background"
  >
    <!-- Header -->
    <div class="flex-shrink-0 border-b border-n-weak bg-n-solid-2">
      <div
        class="flex flex-wrap items-center justify-between gap-4 px-4 py-4 sm:px-6"
      >
        <!-- Title and Tabs -->
        <div class="flex items-center gap-6">
          <div class="flex items-center gap-3">
            <span class="i-lucide-layout-grid text-xl text-n-slate-11" />
            <div>
              <h1 class="text-lg font-semibold text-n-slate-12">Dashboard</h1>
              <p class="text-xs text-n-slate-11">
                {{ $t('ANALYTICS_DASHBOARD.SUBTITLE') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Right Side: Live indicator + Online Badge + Period Selector -->
        <div class="flex items-center gap-2 flex-shrink-0">
          <!-- Live Indicator -->
          <div
            class="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-n-ruby-3 border border-n-ruby-6"
          >
            <span class="w-2 h-2 rounded-full bg-n-ruby-9 animate-pulse" />
            <span class="text-xs font-medium text-n-ruby-11">LIVE</span>
          </div>

          <!-- Online Agents Badge -->
          <div
            class="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg bg-n-alpha-1 border border-n-weak"
          >
            <span class="w-2 h-2 rounded-full bg-n-teal-9 animate-pulse" />
            <span class="text-xs sm:text-sm font-medium text-n-slate-12">
              {{ totalAgentsOnline }} online
            </span>
          </div>

          <!-- Period Selector -->
          <div
            class="flex items-center bg-n-alpha-1 rounded-lg border border-n-weak p-0.5 sm:p-1"
          >
            <button
              v-for="period in periodOptions"
              :key="period.key"
              class="px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium rounded-md transition-colors"
              :class="[
                selectedPeriod === period.key
                  ? 'bg-n-solid-3 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2',
              ]"
              @click="onPeriodChange(period.key)"
            >
              {{ period.label }}
            </button>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div
        class="flex items-center gap-1 px-4 sm:px-6 pb-0 -mb-px overflow-x-auto scrollbar-hide"
      >
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-2.5 text-xs sm:text-sm font-medium rounded-t-lg border-b-2 transition-colors whitespace-nowrap"
          :class="[
            activeTab === tab.key
              ? 'text-n-blue-11 border-n-blue-9 bg-n-alpha-1'
              : 'text-n-slate-11 border-transparent hover:text-n-slate-12 hover:bg-n-alpha-1',
          ]"
          @click="setActiveTab(tab.key)"
        >
          <span :class="tab.icon" class="text-sm sm:text-base" />
          <span class="hidden sm:inline">{{ $t(tab.label) }}</span>
          <span class="sm:hidden">{{ $t(tab.label).split(' ')[0] }}</span>
        </button>
      </div>
    </div>

    <!-- Tab Content -->
    <div class="flex-1 overflow-auto p-4 sm:p-6">
      <component :is="activeTabComponent" />
    </div>
  </section>
</template>
